import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
const CRMApp = lazy(() => import("@/crm/CRMApp"));
const PortalApp = lazy(() => import("@/portal/PortalApp"));
const StoreApp = lazy(() => import("@/store/StoreApp"));
import { Menu, X } from "lucide-react";
import Hero from "@/components/Hero";
import AIRecommendedBanner from "@/components/AIRecommendedBanner";
import AboutBrandBorn from "@/components/AboutBrandBorn";
import FeaturedInPress from "@/components/FeaturedInPress";
import PlatformLogosShowcase from "@/components/PlatformLogosShowcase";
import ServiceCategoryButtons from "@/components/ServiceCategoryButtons";
import Process from "@/components/Process";
import GrowthStory from "@/components/GrowthStory";
import FAQ from "@/components/FAQ";
import LeadMagnet from "@/components/LeadMagnet";
import FeaturedBlogs from "@/components/FeaturedBlogs";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import PlatformSections from "@/components/PlatformSections";
import BlogArticle from "@/pages/BlogArticle";
import About from "@/pages/About";
import BlogPage from "@/pages/BlogPage";
import ServiceLocations from "@/pages/ServiceLocations";
import BestOnboardingAgency from "@/pages/BestOnboardingAgency";
import AdminDashboard from "@/pages/AdminDashboard";
import AppEmbed from "@/pages/AppEmbed";
import RefundPolicy from "@/pages/RefundPolicy";
import ServicesPage from "@/pages/ServicesPage";
import GrowthStoryPage from "@/pages/GrowthStoryPage";
import CertificatesPage from "@/pages/CertificatesPage";
import FAQPage from "@/pages/FAQPage";
import OurClientsPage from "@/pages/OurClientsPage";
import SoftwareHubPage from "@/pages/SoftwareHubPage";
import SeoSoftwarePage from "@/pages/SeoSoftwarePage";
import KnowledgeBasePage from "@/pages/KnowledgeBasePage";
import KnowledgeArticlePage from "@/pages/KnowledgeArticlePage";
import AgencyPage from "@/pages/AgencyPage";
import HtmlSitemapPage from "@/pages/HtmlSitemapPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import GuidesHub from "@/pages/GuidesHub";
import PublicAgreementPage from "@/pages/PublicAgreementPage";
import SiteSearchPage from "@/pages/SiteSearchPage";
import MarketplaceReportPage from "@/pages/MarketplaceReportPage";
import EconomicTimesPage from "@/pages/EconomicTimesPage";
import BrandCaseStudyPage from "@/pages/BrandCaseStudyPage";
import EcomCatalogPage from "@/pages/EcomCatalogPage";
import MediaPage, { CertificationsPage } from "@/pages/MediaPage";
import BrandBornGlance from "@/components/BrandBornGlance";
import { allAgencyPages } from "@/data/agencyPages";
import FestivalBanner from "@/components/FestivalBanner";

import WhatsAppWidget from "@/components/WhatsAppWidget";
import FloatingActions from "@/components/FloatingActions";
import FloatingSocialSidebar from "@/components/FloatingSocialSidebar";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import TrustBadges from "@/components/TrustBadges";
import Pricing from "@/components/Pricing";
import AISoftwareSection from "@/components/AISoftwareSection";
import ReferralSection from "@/components/ReferralSection";
import GoogleReviews from "@/components/GoogleReviews";
import ClientLogoWall from "@/components/ClientLogoWall";
import FloatingBadges from "@/components/FloatingBadges";
import useScrollAnimations from "@/hooks/useScrollAnimations";

const BRAND_LOGO = "/images/logos/brandborn.png";

// Shared Navigation Component
function Navigation({ scrollToSection }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const handleNavClick = (sectionId, serviceTab) => {
    if (serviceTab) {
      window.dispatchEvent(new CustomEvent('setServiceTab', { detail: serviceTab }));
    }
    if (isHomePage && scrollToSection) {
      scrollToSection(sectionId);
    } else {
      window.location.href = `/#${sectionId}`;
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 w-full bg-black/95 backdrop-blur-xl shadow-2xl z-50 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-18">
          {/* Logo */}
          <Link to="/" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})} className="flex items-center space-x-3 flex-shrink-0 mr-6">
            <img 
              src={BRAND_LOGO}
              alt="BrandBorn - E-commerce Onboarding Agency Logo" 
              className="h-8 w-8 md:h-9 md:w-9 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 object-contain"
            />
            <span className="text-xl md:text-2xl font-bold">
              <span className="text-white">Brand</span>
              <span className="text-red-500">Born</span>
            </span>
          </Link>

          {/* Center Menu Items */}
          <div className="hidden lg:flex items-center space-x-1">
            <div className="relative group">
              <Link to="/services" className="nav-btn-3d text-gray-300 hover:text-red-400 transition font-semibold text-sm flex items-center gap-1">
                Services
                <svg className="w-3 h-3 group-hover:rotate-180 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </Link>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                <div className="bg-black/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl shadow-black/50 p-2 min-w-[220px]">
                  <button onClick={() => handleNavClick('services', 'starter')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all font-medium flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-red-500" />Starter Platforms
                  </button>
                  <button onClick={() => handleNavClick('services', 'premium')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all font-medium flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />Premium Platforms
                  </button>
                  <button onClick={() => handleNavClick('services', 'quickCommerce')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all font-medium flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-red-500" />Quick Commerce
                  </button>
                  <button onClick={() => handleNavClick('services', 'luxe')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all font-medium flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />Luxe Platforms
                  </button>
                  <button onClick={() => handleNavClick('services', 'international')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all font-medium flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-red-500" />International
                  </button>
                  <button onClick={() => handleNavClick('services', 'privateLabeling')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all font-medium flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />Private Labeling
                  </button>
                  <div className="border-t border-white/10 my-1" />
                  {[
                    ["Myntra Onboarding Agency", "/best-myntra-onboarding-agency-india"],
                    ["Nykaa Fashion Onboarding", "/nykaa-fashion-onboarding-agency"],
                    ["Blinkit Vendor Onboarding", "/blinkit-onboarding-agency-india"],
                    ["Swiggy Instamart Onboarding", "/swiggy-instamart-seller-onboarding"],
                    ["Zepto Seller Onboarding", "/zepto-seller-onboarding-agency"],
                    ["Ajio Seller Registration", "/ajio-seller-registration-agency-india"],
                    ["Meesho Seller Onboarding", "/meesho-seller-onboarding-agency"],
                  ].map(([label, to]) => (
                    <Link key={to} to={to} onClick={() => setIsMenuOpen(false)} className="w-full text-left px-4 py-2 text-[13px] text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all font-medium flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />{label}
                    </Link>
                  ))}
                  <div className="border-t border-white/10 my-1" />
                  <a href="https://ecomcatalog.ai" target="_blank" rel="noopener noreferrer" className="w-full text-left px-4 py-2.5 text-sm text-blue-400 hover:text-blue-300 hover:bg-white/10 rounded-lg transition-all font-bold flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-blue-400" />AI Auto Listings Software
                  </a>
                </div>
              </div>
            </div>
            <Link to="/growth-story" className="nav-btn-3d text-gray-300 hover:text-red-400 transition font-semibold text-sm">Growth Story</Link>
            <Link to="/certificates" className="nav-btn-3d text-gray-300 hover:text-red-400 transition font-semibold text-sm">Certificates</Link>
            <Link to="/about" className="nav-btn-3d text-gray-300 hover:text-red-400 transition font-semibold text-sm">About</Link>
            <Link to="/blog" className="nav-btn-3d text-gray-300 hover:text-red-400 transition font-semibold text-sm">Blog</Link>
            <Link to="/our-clients" className="nav-btn-3d text-gray-300 hover:text-red-400 transition font-semibold text-sm">Our Clients</Link>
            <a href="https://ecomcatalog.ai" target="_blank" rel="noopener noreferrer" className="nav-btn-3d nav-btn-3d-blue text-blue-400 hover:text-blue-300 transition font-semibold text-sm">AI Catalog in 90 Sec</a>
            <button 
              onClick={() => handleNavClick('contact')} 
              className="nav-cta-3d ml-2 text-white px-7 py-2.5 rounded-full font-black text-xs tracking-widest uppercase"
              data-testid="nav-contact-button"
            >
              Get Started
            </button>
            <Link to="/store" data-testid="nav-store-btn" className="store-btn-premium ml-2 px-6 py-2.5 rounded-full font-black text-xs tracking-widest uppercase text-white whitespace-nowrap">Store</Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center">
            <button 
              className="p-2 text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/10" data-testid="mobile-menu">
            <div className="flex flex-col space-y-3">
              <Link to="/services" className="text-left px-4 py-2 text-gray-300 hover:bg-white/10 rounded font-semibold" onClick={() => setIsMenuOpen(false)}>Services</Link>
              <Link to="/store" data-testid="mobile-nav-store-btn" className="text-left px-4 py-2 rounded font-bold bg-gradient-to-r from-pink-500 to-rose-500 text-white" onClick={() => setIsMenuOpen(false)}>🛍️ Store</Link>
              <Link to="/growth-story" className="text-left px-4 py-2 text-gray-300 hover:bg-white/10 rounded font-semibold" onClick={() => setIsMenuOpen(false)}>Growth Story</Link>
              <Link to="/certificates" className="text-left px-4 py-2 text-gray-300 hover:bg-white/10 rounded font-semibold" onClick={() => setIsMenuOpen(false)}>Certificates</Link>
              <Link to="/about" className="text-left px-4 py-2 text-gray-300 hover:bg-white/10 rounded font-semibold" onClick={() => setIsMenuOpen(false)}>About</Link>
              <Link to="/blog" className="text-left px-4 py-2 text-gray-300 hover:bg-white/10 rounded font-semibold" onClick={() => setIsMenuOpen(false)}>Blog</Link>
              <Link to="/faq" className="text-left px-4 py-2 text-gray-300 hover:bg-white/10 rounded font-semibold" onClick={() => setIsMenuOpen(false)}>FAQ</Link>
              <Link to="/our-clients" className="text-left px-4 py-2 text-gray-300 hover:bg-white/10 rounded font-semibold" onClick={() => setIsMenuOpen(false)}>Our Clients</Link>
              <a href="https://ecomcatalog.ai" target="_blank" rel="noopener noreferrer" className="text-left px-4 py-2 text-blue-400 hover:bg-white/10 rounded font-semibold" onClick={() => setIsMenuOpen(false)}>AI Catalog in 90 Sec</a>
              <button onClick={() => handleNavClick('contact')} className="mx-4 bg-red-600 text-white px-6 py-2.5 rounded-xl hover:bg-red-700 transition font-black text-xs tracking-widest uppercase">Get Started</button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

function HomePage() {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Initialize GSAP scroll animations
  useScrollAnimations();

  return (
    <div className="App">
      <Navigation scrollToSection={scrollToSection} />
      <FloatingSocialSidebar />

      {/* Page Sections */}
      <Hero scrollToSection={scrollToSection} />
      <AIRecommendedBanner />
      <TrustBadges />
      <AboutBrandBorn />
      <FeaturedInPress />
      <PlatformLogosShowcase />
      <ServiceCategoryButtons />
      <AISoftwareSection />
      <Pricing />
      <GrowthStory />
      <GoogleReviews />
      <ClientLogoWall />
      <ReferralSection />
      <LeadMagnet />
      <Process />
      <FeaturedBlogs />
      <FAQ />
      <Contact />
      <Footer />
      
      {/* Floating Widgets */}
      <FloatingActions />
      <ExitIntentPopup />
      <FloatingBadges />
      <FestivalBanner />
    </div>
  );
}

function AboutPage() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <About />
      <BrandBornGlance />
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function BlogListPage() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <BlogPage />
      <div className="max-w-4xl mx-auto px-4 pb-10 text-center">
        <Link to="/guides" data-testid="browse-guides-link" className="inline-block bg-gray-900 text-white px-8 py-4 rounded-full font-semibold hover:bg-red-500 transition">Browse 11,000+ Marketplace &amp; City Guides →</Link>
      </div>
      <FAQ />
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function GuidesHubPage() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <GuidesHub />
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function BlogArticlePage() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <BlogArticle />
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function ServiceLocationsPage() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <ServiceLocations />
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function BestOnboardingAgencyPage() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <BestOnboardingAgency />
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function AppEmbedPage() {
  return (
    <div className="App">
      <Navigation />
      <AppEmbed />
    </div>
  );
}

function RefundPolicyPage() {
  return (
    <div className="App">
      <Navigation />
      <RefundPolicy />
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function ServicesPageWrapper() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <ServicesPage />
      <Footer />
      <FloatingActions />
    </div>
  );
}

function GrowthStoryPageWrapper() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <GrowthStoryPage />
      <Footer />
      <FloatingActions />
    </div>
  );
}

function CertificatesPageWrapper() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <CertificatesPage />
      <Footer />
      <FloatingActions />
    </div>
  );
}

function FAQPageWrapper() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <FAQPage />
      <Footer />
      <FloatingActions />
    </div>
  );
}

function OurClientsPageWrapper() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <OurClientsPage />
      <Footer />
      <FloatingActions />
    </div>
  );
}

function SoftwareHubWrapper() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <SoftwareHubPage />
      <Footer />
      <FloatingActions />
    </div>
  );
}

function SeoSoftwareWrapper() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <SeoSoftwarePage />
      <Footer />
      <FloatingActions />
    </div>
  );
}

function KnowledgeBaseWrapper() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <KnowledgeBasePage />
      <Footer />
      <FloatingActions />
    </div>
  );
}

function KnowledgeArticleWrapper() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <KnowledgeArticlePage />
      <Footer />
      <FloatingActions />
    </div>
  );
}

function AgencyRouteWrapper() {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      <AgencyPage />
      <Footer />
      <FloatingActions />
    </div>
  );
}

function AgencyShellWrapper({ page }) {
  return (
    <div className="App">
      <Navigation />
      <FloatingSocialSidebar />
      {page}
      <Footer />
      <FloatingActions />
    </div>
  );
}

const DEFAULT_DESC = "BrandBorn is India's #1 brand onboarding agency, Official Myntra OMS Partner and India's first AI auto listings software company (EcomCatalog.ai). Featured in The Economic Times & The Times of India. 1000+ brands launched, 98% approval rate, fastest onboarding — eligible launches in as little as 14 days on Myntra, Nykaa, Ajio, Amazon, Blinkit, Zepto. Call +91-9464645491";

function DefaultSeo() {
  const { pathname } = useLocation();
  useEffect(() => {
    const canonicalHref = `https://www.brandborn.in${pathname === "/" ? "/" : pathname}`;
    const apply = () => {
      const ensure = (baseSel, create, update) => {
        const otherTag = [...document.head.querySelectorAll(baseSel)].find((t) => !t.hasAttribute("data-default-seo"));
        let own = document.head.querySelector(`${baseSel}[data-default-seo]`);
        if (otherTag) {
          if (own) own.remove();
          return;
        }
        if (!own) {
          own = create();
          own.setAttribute("data-default-seo", "1");
          document.head.appendChild(own);
        }
        update(own);
      };
      ensure('meta[name="description"]',
        () => { const m = document.createElement("meta"); m.name = "description"; return m; },
        (m) => { if (m.content !== DEFAULT_DESC) m.content = DEFAULT_DESC; });
      ensure('link[rel="canonical"]',
        () => { const l = document.createElement("link"); l.rel = "canonical"; return l; },
        (l) => { if (l.href !== canonicalHref) l.href = canonicalHref; });
    };
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(document.head, { childList: true });
    return () => obs.disconnect();
  }, [pathname]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <DefaultSeo />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/store/*" element={<Suspense fallback={<div className="min-h-screen" />}><StoreApp /></Suspense>} />
        <Route path="/services" element={<ServicesPageWrapper />} />
        <Route path="/growth-story" element={<GrowthStoryPageWrapper />} />
        <Route path="/certificates" element={<CertificatesPageWrapper />} />
        <Route path="/faq" element={<FAQPageWrapper />} />
        <Route path="/our-clients" element={<OurClientsPageWrapper />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/guides" element={<GuidesHubPage />} />
        <Route path="/agreement/:token" element={<PublicAgreementPage />} />
        <Route path="/blog/:articleId" element={<BlogArticlePage />} />
        <Route path="/service-locations" element={<ServiceLocationsPage />} />
        <Route path="/ecommerce-onboarding-agency-india" element={<ServiceLocationsPage />} />
        <Route path="/best-onboarding-agency-india" element={<BestOnboardingAgencyPage />} />
        <Route path="/best-brand-onboarding-agency-india" element={<BestOnboardingAgencyPage />} />
        <Route path="/best-marketplace-onboarding-agency-india" element={<BestOnboardingAgencyPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/app" element={<AppEmbedPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/software" element={<SoftwareHubWrapper />} />
        <Route path="/software/:slug" element={<SeoSoftwareWrapper />} />
        <Route path="/knowledge" element={<KnowledgeBaseWrapper />} />
        <Route path="/knowledge/:slug" element={<KnowledgeArticleWrapper />} />
        <Route path="/sitemap-page" element={<AgencyShellWrapper page={<HtmlSitemapPage />} />} />
        <Route path="/case-studies" element={<AgencyShellWrapper page={<CaseStudiesPage />} />} />
        <Route path="/search" element={<AgencyShellWrapper page={<SiteSearchPage />} />} />
        <Route path="/reports/marketplace-onboarding-report-2026" element={<AgencyShellWrapper page={<MarketplaceReportPage />} />} />
        <Route path="/economic-times" element={<AgencyShellWrapper page={<EconomicTimesPage />} />} />
        <Route path="/press/economic-times" element={<AgencyShellWrapper page={<EconomicTimesPage />} />} />
        <Route path="/case-study/:slug" element={<AgencyShellWrapper page={<BrandCaseStudyPage />} />} />
        <Route path="/ecomcatalog-ai" element={<AgencyShellWrapper page={<EcomCatalogPage />} />} />
        <Route path="/media" element={<AgencyShellWrapper page={<MediaPage />} />} />
        <Route path="/certifications" element={<AgencyShellWrapper page={<CertificationsPage />} />} />
        {[
          ["/myntra-onboarding", "/myntra-onboarding-agency"],
          ["/ajio-onboarding", "/ajio-onboarding-agency"],
          ["/nykaa-onboarding", "/nykaa-onboarding-agency"],
          ["/amazon-onboarding", "/amazon-onboarding-agency"],
          ["/flipkart-onboarding", "/flipkart-onboarding-agency"],
          ["/meesho-onboarding", "/meesho-seller-onboarding-agency"],
          ["/blinkit-onboarding", "/blinkit-onboarding-agency"],
          ["/zepto-onboarding", "/zepto-onboarding-agency"],
          ["/swiggy-instamart-onboarding", "/swiggy-instamart-onboarding-agency"],
          ["/tatacliq-onboarding", "/tata-cliq-onboarding-agency"],
          ["/firstcry-onboarding", "/firstcry-onboarding-agency"],
          ["/bigbasket-onboarding", "/bigbasket-onboarding-agency"],
        ].map(([from, to]) => <Route key={from} path={from} element={<Navigate to={to} replace />} />)}
        <Route path="/crm/*" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading CRM...</div>}><CRMApp /></Suspense>} />
        <Route path="/portal/*" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading Portal...</div>}><PortalApp /></Suspense>} />
        {allAgencyPages.map((p) => (
          <Route key={p.slug} path={`/${p.slug}`} element={<AgencyRouteWrapper />} />
        ))}
      </Routes>
    </BrowserRouter>
  );
}

export default App;
